import { Head } from "@unhead/react";
import { Link } from "react-router-dom";

const APP_STYLE = {
  page: {
    background: "#0a0a0a",
    minHeight: "100vh",
    color: "#f0ede8",
    overflowX: "hidden",
  },
  container: {
    width: "100%",
    maxWidth: 780,
    margin: "0 auto",
    padding: "48px 16px 72px",
    boxSizing: "border-box",
  },
  card: {
    background: "rgba(255,255,255,0.03)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: 12,
    padding: "22px 18px",
    marginBottom: 20,
    boxSizing: "border-box",
    overflow: "hidden",
  },
  tag: (color) => ({
    display: "inline-block",
    background: color,
    color: "#fff",
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    padding: "3px 10px",
    borderRadius: 4,
    marginBottom: 12,
    maxWidth: "100%",
    wordBreak: "break-word",
  }),
  h1: {
    fontFamily: "Georgia, serif",
    fontSize: "2rem",
    fontWeight: 900,
    lineHeight: 1.15,
    color: "#f0ede8",
    marginBottom: 16,
  },
  h2: {
    fontFamily: "Georgia, serif",
    fontSize: "1.45rem",
    fontWeight: 700,
    color: "#f0ede8",
    marginBottom: 24,
  },
  h3: {
    fontSize: "1.2rem",
    fontWeight: 700,
    color: "#f0ede8",
    marginBottom: 8,
    fontFamily: "Georgia, serif",
    lineHeight: 1.3,
  },
  p: {
    color: "rgba(240,237,232,0.6)",
    lineHeight: 1.7,
    fontSize: 15,
    marginBottom: 12,
  },
  intro: {
    color: "rgba(240,237,232,0.65)",
    fontSize: 16,
    lineHeight: 1.75,
    marginBottom: 12,
  },
  pros: {
    color: "rgba(240,237,232,0.55)",
    fontSize: 14,
    lineHeight: 1.8,
    margin: 0,
    padding: 0,
    listStyle: "none",
  },
  cons: {
    color: "rgba(240,237,232,0.4)",
    fontSize: 14,
    lineHeight: 1.8,
    margin: 0,
    padding: 0,
    listStyle: "none",
  },
  sectionLabel: {
    fontSize: 11,
    letterSpacing: "0.15em",
    textTransform: "uppercase",
    color: "#F7931A",
    marginBottom: 16,
    fontWeight: 600,
  },
};

function AppCard({
  rank,
  tag,
  tagColor,
  name,
  description,
  pros,
  cons,
  bestFor,
  feeNote,
}) {
  return (
    <div style={APP_STYLE.card}>
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 8,
          marginBottom: 8,
        }}
      >
        <span style={APP_STYLE.tag(tagColor)}>{tag}</span>
        <span style={{ color: "rgba(240,237,232,0.25)", fontSize: 13 }}>
          #{rank}
        </span>
      </div>

      <h3 style={APP_STYLE.h3}>{name}</h3>
      <p style={APP_STYLE.p}>{description}</p>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: 16,
          marginTop: 12,
        }}
      >
        <div style={{ minWidth: 0 }}>
          <p
            style={{
              fontSize: 11,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              color: "#F7931A",
              marginBottom: 6,
              fontWeight: 600,
            }}
          >
            Strengths
          </p>

          <ul style={APP_STYLE.pros}>
            {pros.map((p, i) => (
              <li key={i}>✓ {p}</li>
            ))}
          </ul>
        </div>

        <div style={{ minWidth: 0 }}>
          <p
            style={{
              fontSize: 11,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              color: "rgba(240,237,232,0.3)",
              marginBottom: 6,
              fontWeight: 600,
            }}
          >
            Tradeoffs
          </p>

          <ul style={APP_STYLE.cons}>
            {cons.map((c, i) => (
              <li key={i}>– {c}</li>
            ))}
          </ul>
        </div>
      </div>

      <p
        style={{
          marginTop: 16,
          marginBottom: 6,
          fontSize: 13,
          color: "rgba(240,237,232,0.4)",
          lineHeight: 1.6,
        }}
      >
        <strong style={{ color: "rgba(240,237,232,0.6)" }}>
          Best for:
        </strong>{" "}
        {bestFor}
      </p>

      {feeNote && (
        <p
          style={{
            margin: 0,
            fontSize: 12,
            color: "rgba(240,237,232,0.32)",
            lineHeight: 1.6,
          }}
        >
          <strong style={{ color: "rgba(240,237,232,0.48)" }}>
            Pricing note:
          </strong>{" "}
          {feeNote}
        </p>
      )}
    </div>
  );
}

const COMPARISON_ROWS = [
  ["SatStacker", "Yes", "Smart Timing", "Strike-connected", "$1.99/mo + 0.25%/trade"],
  ["River", "Yes", "Supercharged buys", "Auto-withdrawal", "Recurring buys can become fee-free"],
  ["Swan Bitcoin", "Yes", "No built-in dip trigger", "Auto-withdrawal", "1% buys"],
  ["Strike", "Yes", "Target orders", "Send to own wallet", "Check current trade preview"],
  ["Coinbase", "Yes", "No DCA timing feature", "Manual withdrawal", "Variable"],
];

const COMPARISON_COLS = [
  "Recurring buys",
  "Dip-aware option",
  "Self-custody path",
  "Fee model",
];

function ComparisonRowsMobile() {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(215px, 1fr))",
        gap: 10,
      }}
    >
      {COMPARISON_ROWS.map(([app, ...cols], i) => (
        <div
          key={app}
          style={{
            border:
              i === 0
                ? "1px solid rgba(247,147,26,0.3)"
                : "1px solid rgba(255,255,255,0.07)",
            borderRadius: 10,
            padding: "16px 14px",
            background:
              i === 0
                ? "rgba(247,147,26,0.05)"
                : "rgba(255,255,255,0.02)",
          }}
        >
          <div
            style={{
              color: i === 0 ? "#F7931A" : "#f0ede8",
              fontWeight: 600,
              fontSize: 15,
              marginBottom: 12,
            }}
          >
            {app}
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {COMPARISON_COLS.map((label, j) => (
              <div
                key={label}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  gap: 12,
                  fontSize: 12.5,
                }}
              >
                <span style={{ color: "rgba(240,237,232,0.4)" }}>
                  {label}
                </span>
                <span
                  style={{
                    color:
                      j === 1 && cols[j] !== "No built-in dip trigger" && cols[j] !== "No DCA timing feature"
                        ? "#F7931A"
                        : "rgba(240,237,232,0.72)",
                    fontWeight: 600,
                    textAlign: "right",
                    maxWidth: "56%",
                  }}
                >
                  {cols[j]}
                </span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function BestBitcoinApps2026() {
  const title =
    "Best Bitcoin DCA Apps in 2026: SatStacker vs River vs Swan vs Strike";

  const description =
    "Compare the best Bitcoin DCA apps in 2026 for recurring buys, Smart DCA, self-custody options, automation, and fees. SatStacker, River, Swan, Strike, and Coinbase compared.";

  const canonical =
    "https://satstacker.app/blog/best-bitcoin-apps-2026";

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: "Best Bitcoin DCA Apps in 2026",
    description,
    author: {
      "@type": "Organization",
      name: "SatStacker",
      url: "https://satstacker.app",
    },
    publisher: {
      "@type": "Organization",
      name: "SatStacker",
      url: "https://satstacker.app",
    },
    datePublished: "2026-03-23",
    dateModified: "2026-09-22",
    mainEntityOfPage: canonical,
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "SatStacker",
        item: "https://satstacker.app/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Blog",
        item: "https://satstacker.app/blog",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: "Best Bitcoin DCA Apps in 2026",
        item: canonical,
      },
    ],
  };

  return (
    <div style={APP_STYLE.page}>
      <Head>
        <title>{title}</title>

        <meta
          name="description"
          content={description}
        />

        <link
          rel="canonical"
          href={canonical}
        />

        <meta property="og:type" content="article" />
        <meta property="og:title" content={title} />
        <meta
          property="og:description"
          content={description}
        />
        <meta property="og:url" content={canonical} />
        <meta property="og:site_name" content="SatStacker" />

        <meta
          name="twitter:card"
          content="summary_large_image"
        />
        <meta
          name="twitter:title"
          content={title}
        />
        <meta
          name="twitter:description"
          content={description}
        />

        <script type="application/ld+json">
          {JSON.stringify(articleSchema)}
        </script>

        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Head>

      <div style={APP_STYLE.container}>
        <p
          style={{
            fontSize: 13,
            color: "rgba(240,237,232,0.35)",
            marginBottom: 28,
          }}
        >
          <Link
            to="/blog"
            style={{ color: "#F7931A", textDecoration: "none" }}
          >
            Blog
          </Link>{" "}
          / Best Bitcoin DCA Apps 2026
        </p>

        <p
          style={{
            fontSize: 12,
            letterSpacing: "0.2em",
            textTransform: "uppercase",
            color: "#F7931A",
            marginBottom: 16,
            fontWeight: 500,
          }}
        >
          Bitcoin DCA Apps · 2026 Comparison
        </p>

        <h1 style={APP_STYLE.h1}>
          Best Bitcoin DCA Apps in 2026
        </h1>

        <p
          style={{
            color: "rgba(240,237,232,0.45)",
            fontSize: 13,
            marginBottom: 24,
          }}
        >
          Updated September 2026 · 10 min read
        </p>

        <p style={APP_STYLE.intro}>
          If your goal is to buy Bitcoin automatically on a recurring
          schedule, you do not necessarily need a full trading platform.
          A good Bitcoin DCA app should make recurring purchases easy,
          keep fees understandable, and give you a clear path to moving
          Bitcoin to your own wallet.
        </p>

        <p style={{ ...APP_STYLE.intro, marginBottom: 28 }}>
          We compared SatStacker, River, Swan Bitcoin, Strike, and
          Coinbase for recurring-buy automation, dip-aware execution,
          self-custody options, pricing, and ease of use.
        </p>

        <div
          style={{
            background: "rgba(247,147,26,0.06)",
            border: "1px solid rgba(247,147,26,0.18)",
            borderRadius: 12,
            padding: "20px 18px",
            marginBottom: 24,
          }}
        >
          <p style={APP_STYLE.sectionLabel}>
            Disclosure
          </p>

          <p style={{ ...APP_STYLE.p, marginBottom: 0 }}>
            SatStacker publishes this comparison and is one of the products
            included. We therefore have an obvious commercial interest in the
            category. The comparison criteria are stated below so you can
            evaluate the tradeoffs yourself rather than relying only on our
            ordering.
          </p>
        </div>

        <div
          style={{
            background: "rgba(255,255,255,0.03)",
            border: "1px solid rgba(255,255,255,0.08)",
            borderRadius: 12,
            padding: "20px 18px",
            marginBottom: 40,
          }}
        >
          <p style={APP_STYLE.sectionLabel}>
            How we evaluated these apps
          </p>

          <p style={APP_STYLE.p}>
            We focused on the features that matter most to a long-term
            recurring Bitcoin buyer:
          </p>

          <ul
            style={{
              color: "rgba(240,237,232,0.58)",
              fontSize: 14,
              lineHeight: 1.9,
              paddingLeft: 20,
              margin: 0,
            }}
          >
            <li>Recurring-buy support and available schedules</li>
            <li>Whether the app offers dip-aware or conditional execution</li>
            <li>How easily Bitcoin can move toward self-custody</li>
            <li>Current pricing and recurring-buy fee structure</li>
            <li>Whether the product is Bitcoin-focused or multi-asset</li>
            <li>How much manual intervention the strategy requires</li>
          </ul>
        </div>

        <div
          style={{
            background: "rgba(255,255,255,0.03)",
            border: "1px solid rgba(255,255,255,0.08)",
            borderRadius: 12,
            padding: "20px 16px",
            marginBottom: 48,
            boxSizing: "border-box",
          }}
        >
          <p style={APP_STYLE.sectionLabel}>
            Quick Comparison
          </p>

          <ComparisonRowsMobile />

          <p
            style={{
              marginTop: 14,
              marginBottom: 0,
              color: "rgba(240,237,232,0.32)",
              fontSize: 12,
              lineHeight: 1.6,
            }}
          >
            Pricing and features change. We checked the official provider
            information available in September 2026; always confirm current
            terms before opening or changing an account.
          </p>
        </div>

        <h2 style={APP_STYLE.h2}>
          The Best Bitcoin DCA Apps in 2026
        </h2>

        <AppCard
          rank={1}
          tag="Best for Smart DCA"
          tagColor="#F7931A"
          name="SatStacker"
          description="SatStacker is built specifically around recurring Bitcoin accumulation. You choose a daily, weekly, or bi-weekly budget and connect your existing Strike account. Smart Timing then looks for better Bitcoin prices inside each scheduled buy window instead of automatically executing the full allocation when the window opens."
          pros={[
            "Smart Timing inside each DCA window",
            "Same planned budget and cadence",
            "Uses an existing Strike account",
            "SatStacker does not custody user funds",
            "Web, iOS, and Android access",
          ]}
          cons={[
            "Requires a Strike account",
            "Smart Timing is a paid feature",
            "Newer product with a smaller user base",
          ]}
          bestFor="Bitcoin DCA users who want automated execution timing without manually watching charts"
          feeNote="$0 for the first month of Smart Timing, then $1.99/month plus 0.25% per trade."
        />

        <AppCard
          rank={2}
          tag="Best for Zero-Fee Recurring Buys"
          tagColor="#7c3aed"
          name="River"
          description="River is a Bitcoin-only brokerage with recurring buys, auto-withdrawals, and Supercharged Recurring Buys. Its standard recurring buys can become zero-fee after the initial qualifying period, while Supercharged buys can automatically increase the purchase amount when River detects a qualifying dip at the scheduled buy time."
          pros={[
            "Bitcoin-only platform",
            "Hourly, daily, weekly, bi-weekly, and monthly recurring options",
            "Recurring buys can become zero-fee",
            "Supercharged Recurring Buys respond to dips",
            "Bitcoin auto-withdrawals available",
          ]}
          cons={[
            "Supercharged buys increase the amount invested during qualifying dips",
            "Different approach from fixed-budget Smart Timing",
            "Availability depends on supported jurisdictions",
          ]}
          bestFor="Bitcoin buyers who want a mature Bitcoin-only platform with recurring buys and optional dip-based purchase increases"
          feeNote="River states that standard recurring buys become zero-fee after the initial qualifying period; check current terms for the first order and other order types."
        />

        <AppCard
          rank={3}
          tag="Best for Straightforward Bitcoin Savings"
          tagColor="#1d4ed8"
          name="Swan Bitcoin"
          description="Swan is a Bitcoin-focused savings platform with recurring purchase plans and automatic withdrawals to a wallet you control. Its recurring-buy experience is straightforward rather than built around dip-triggered execution."
          pros={[
            "Bitcoin-focused product",
            "Recurring purchase plans",
            "Automatic Bitcoin withdrawals",
            "Established education and support resources",
            "Simple long-term savings approach",
          ]}
          cons={[
            "No Smart Timing-style execution inside each DCA window",
            "1% buy fee under Swan's current published pricing",
            "Purchased Bitcoin may be held with a custodian until withdrawal",
          ]}
          bestFor="Long-term Bitcoin savers who prioritize a simple recurring plan and automated withdrawals"
          feeNote="Swan currently publishes a 1% fee on buys and $0 withdrawal fees."
        />

        <AppCard
          rank={4}
          tag="Best for Native Strike Users"
          tagColor="#059669"
          name="Strike"
          description="Strike supports native recurring Bitcoin purchases as well as target orders. Recurring purchases can be scheduled at supported frequencies, and users can later send Bitcoin to their own custody. SatStacker connects to Strike for users who want an additional Smart Timing layer on top of that account."
          pros={[
            "Native recurring Bitcoin purchases",
            "Target-price orders",
            "Bitcoin and Lightning functionality",
            "Can send Bitcoin to your own wallet",
            "Also serves as the connected account used by SatStacker",
          ]}
          cons={[
            "Native recurring buys execute on the schedule you set",
            "No SatStacker-style Smart Timing window unless using SatStacker",
            "Fees and program terms can vary, so the trade preview matters",
          ]}
          bestFor="People who already use Strike and want native recurring purchases or target orders"
          feeNote="Strike's published materials describe recurring-buy fee treatment that can depend on current program terms; review the in-app trade preview for the applicable cost."
        />

        <AppCard
          rank={5}
          tag="Best for Broad Crypto Access"
          tagColor="#374151"
          name="Coinbase"
          description="Coinbase supports recurring purchases and is designed for easy access to Bitcoin alongside many other crypto assets. It is broader than a Bitcoin-only DCA product and holds purchased assets in the Coinbase account until the user chooses to withdraw them."
          pros={[
            "Recurring buy support",
            "Large, established platform",
            "Beginner-friendly interface",
            "Supports many assets beyond Bitcoin",
            "Bitcoin can be withdrawn to an external wallet",
          ]}
          cons={[
            "Not Bitcoin-only",
            "No Smart DCA timing feature for recurring buys",
            "Fees vary by payment method, order size, market conditions, and other factors",
          ]}
          bestFor="Beginners who want one mainstream account for Bitcoin and other crypto assets"
          feeNote="Coinbase says fees are shown before confirmation and vary based on factors such as payment method, order size, market conditions, jurisdiction, and asset."
        />

        <div
          style={{
            background: "rgba(247,147,26,0.08)",
            border: "1px solid rgba(247,147,26,0.2)",
            borderRadius: 12,
            padding: "28px 22px",
            margin: "48px 0",
            boxSizing: "border-box",
          }}
        >
          <p style={APP_STYLE.sectionLabel}>
            Which Bitcoin DCA app fits which user?
          </p>

          <p
            style={{
              fontFamily: "Georgia, serif",
              fontSize: "1.12rem",
              color: "#f0ede8",
              lineHeight: 1.6,
              marginBottom: 12,
            }}
          >
            SatStacker is built for people who already want to DCA Bitcoin
            and specifically want execution timing inside each buying window.
          </p>

          <p style={APP_STYLE.p}>
            River is a strong alternative if you want a Bitcoin-only brokerage
            with zero-fee recurring buys and the option to invest more when a
            scheduled buy qualifies for Supercharging. Swan is well suited to
            straightforward Bitcoin savings and automatic withdrawals. Strike
            is a natural fit for users who want recurring purchases and target
            orders directly in Strike, while Coinbase is the broadest option
            for people who also want access to many other crypto assets.
          </p>

          <p style={{ ...APP_STYLE.p, marginBottom: 0 }}>
            The key difference for SatStacker is that Smart Timing is designed
            to work with the same planned DCA budget rather than increasing the
            amount invested during a dip.
          </p>
        </div>

        <h2 style={APP_STYLE.h2}>
          Bitcoin DCA App FAQ
        </h2>

        {[
          {
            q: "What should I look for in a Bitcoin DCA app?",
            a: "Look at recurring-buy schedules, fees, custody and withdrawal options, whether the app is Bitcoin-only or multi-asset, and how much manual work the strategy requires. If you want dip-aware automation, also check whether the feature changes execution timing or increases the amount you invest.",
          },
          {
            q: "Which Bitcoin apps support recurring buys?",
            a: "SatStacker, River, Swan, Strike, and Coinbase all support recurring Bitcoin accumulation in some form. Their schedules, fee models, custody workflows, and execution logic differ.",
          },
          {
            q: "Which Bitcoin DCA apps react to price dips automatically?",
            a: "SatStacker Smart Timing looks for better prices inside a scheduled buy window while keeping the planned recurring budget. River's Supercharged Recurring Buys can increase the purchase amount when a qualifying dip is detected at the scheduled buy time. These are different approaches to dip-aware automation.",
          },
          {
            q: "Do Bitcoin DCA apps let me move Bitcoin to my own wallet?",
            a: "The products compared here provide a path to external or self-custody, but the workflow differs. Some support automatic withdrawals, while others require a manual send. Review withdrawal timing, minimums, and network fees before choosing a platform.",
          },
          {
            q: "Is Smart DCA the same as regular recurring buying?",
            a: "No. Regular DCA generally executes a fixed amount on a fixed schedule. Smart DCA keeps the recurring plan but adds execution rules inside the buying window. Read our Smart DCA guide for a detailed explanation.",
          },
        ].map(({ q, a }) => (
          <div
            key={q}
            style={{
              borderBottom: "1px solid rgba(255,255,255,0.06)",
              paddingBottom: 20,
              marginBottom: 20,
            }}
          >
            <h3
              style={{
                fontFamily: "Georgia, serif",
                fontSize: "1.05rem",
                fontWeight: 700,
                color: "#f0ede8",
                marginBottom: 8,
                lineHeight: 1.35,
              }}
            >
              {q}
            </h3>

            <p
              style={{
                color: "rgba(240,237,232,0.55)",
                fontSize: 15,
                lineHeight: 1.7,
              }}
            >
              {a}
            </p>
          </div>
        ))}

        <div
          style={{
            background: "rgba(255,255,255,0.025)",
            border: "1px solid rgba(255,255,255,0.07)",
            borderRadius: 12,
            padding: "22px 18px",
            marginTop: 40,
          }}
        >
          <p style={APP_STYLE.sectionLabel}>
            Sources checked September 2026
          </p>

          <p style={APP_STYLE.p}>
            Product features and pricing can change. For the competitor
            details above, we checked the providers' official documentation:
          </p>

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 9,
              fontSize: 13,
            }}
          >
            <a
              href="https://river.com/zero-fee"
              target="_blank"
              rel="noreferrer"
              style={{ color: "#F7931A", textDecoration: "none" }}
            >
              River recurring buys and pricing →
            </a>

            <a
              href="https://support.river.com/hc/en-us/articles/50061674534675-What-are-supercharged-recurring-buys"
              target="_blank"
              rel="noreferrer"
              style={{ color: "#F7931A", textDecoration: "none" }}
            >
              River Supercharged Recurring Buys →
            </a>

            <a
              href="https://www.swanbitcoin.com/fees/"
              target="_blank"
              rel="noreferrer"
              style={{ color: "#F7931A", textDecoration: "none" }}
            >
              Swan pricing →
            </a>

            <a
              href="https://strike.me/learn/how-to-buy-bitcoin/"
              target="_blank"
              rel="noreferrer"
              style={{ color: "#F7931A", textDecoration: "none" }}
            >
              Strike recurring purchases and target orders →
            </a>

            <a
              href="https://help.coinbase.com/en/coinbase/trading-and-funding/buying-selling-or-converting-crypto/how-can-i-create-or-cancel-a-recurring-transaction"
              target="_blank"
              rel="noreferrer"
              style={{ color: "#F7931A", textDecoration: "none" }}
            >
              Coinbase recurring buys →
            </a>
          </div>
        </div>

        <div style={{ textAlign: "center", marginTop: 56 }}>
          <p
            style={{
              fontFamily: "Georgia, serif",
              fontSize: "1.4rem",
              fontWeight: 700,
              color: "#f0ede8",
              marginBottom: 12,
              lineHeight: 1.3,
            }}
          >
            Want to compare Smart Timing with your own DCA?
          </p>

          <p
            style={{
              color: "rgba(240,237,232,0.5)",
              fontSize: 15,
              marginBottom: 24,
              lineHeight: 1.7,
            }}
          >
            Use the historical calculator before creating an account.
          </p>

          <Link
            to="/calculator"
            style={{
              display: "inline-block",
              background: "#F7931A",
              color: "#0a0a0a",
              fontWeight: 700,
              fontSize: 15,
              padding: "14px 28px",
              borderRadius: 8,
              textDecoration: "none",
              maxWidth: "100%",
              boxSizing: "border-box",
            }}
          >
            Try the Bitcoin DCA Calculator →
          </Link>

          <div
            style={{
              marginTop: 28,
              display: "flex",
              justifyContent: "center",
              flexWrap: "wrap",
              gap: "8px 18px",
              fontSize: 13,
            }}
          >
            <Link
              to="/bitcoin-dca"
              style={{ color: "#F7931A", textDecoration: "none" }}
            >
              How to DCA Bitcoin
            </Link>

            <Link
              to="/smart-dca"
              style={{ color: "#F7931A", textDecoration: "none" }}
            >
              Smart DCA Guide
            </Link>

            <Link
              to="/blog/smart-dca-vs-regular-dca"
              style={{ color: "#F7931A", textDecoration: "none" }}
            >
              Smart DCA vs Regular DCA
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
